import toast from "react-hot-toast";

const server = process.env.REACT_APP_SERVER_URL || "";

interface SubscriptionData {
  priceId: string;
  trial_periode: number;
}

interface CancelSubscriptionPayload {
  userId: number;
  subscriptionId: string;
  option: "immediate" | "end_of_period" | "custom_date";
  customDate?: string;
}

interface CancelSubscriptionResponse {
  success: boolean;
  message: string;
  data: {
    subscriptionId: string;
    status: string;
    cancel_at_period_end: boolean;
    cancel_at: string | null;
  };
}

interface RefundSubscriptionPayload {
  payment_id: string;
  amount: any;
}

interface RefundSubscriptionResponse {
  success: boolean;
  message: string;
}

export const subscription = {
  // Subscription payment with Stripe
  paySubscription: async (formData: SubscriptionData): Promise<string> => {
    try {
      const response = await fetch(`${server}/api/subscription/stripe-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const error = new Error(errorText || `HTTP error! Status: ${response.status}`);

        if (response.status === 409) {
          (error as any).sessionUrl = "/abonnement/deja-actif";
        }

        throw error;
      }

      const data = await response.json();
      if (!data.sessionUrl) {
        throw new Error("No session URL returned from server");
      }

      return data.sessionUrl;
    } catch (error: any) {
      console.error("Subscription error:", error);
      toast.error(`Erreur lors de l'abonnement: ${error.message || "Une erreur est survenue"}`);
      throw error;
    }
  },

  // Create a new plan
  createPlan: async (formData: any) => {
    try {
      const response = await fetch(`${server}/api/subscription/create-plan`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error?.[0]?.msg || errorData.message || `HTTP error! Status: ${response.status}`
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error?.[0]?.msg || "Échec de la création du plan");
      }

      return data;
    } catch (error: any) {
      console.error("Plan creation error:", error);
      toast.error(`Erreur lors de la création du plan: ${error.message || "Une erreur est survenue"}`);
      throw error;
    }
  },
  // Admin get all plans
  adminGetAllPlans: async (page = 1, searchTerm = "") => {
    const query = new URLSearchParams({ page: page.toString() });
    if (searchTerm) {
      query.append("searchTerm", searchTerm);
    }

    const result = await fetch(`${server}/api/admin/subscription/plans?${query.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
      },
    });

    return result.json();
  },

  // Admin get all plans
  adminGetAllSubscriptions: async ({
    limit = 10,
    starting_after,
    ending_before,
    email,
    status,
  }: {
    limit?: number;
    starting_after?: string;
    ending_before?: string;
    email?: string; // <-- ADD this
    status?: string;
  }) => {
    const query = new URLSearchParams();
    query.append("limit", limit.toString());
    if (starting_after) query.append("starting_after", starting_after);
    if (ending_before) query.append("ending_before", ending_before);
    if (email) query.append("email", email); // <-- used correctly here
    if (status) query.append("status", status);

    const result = await fetch(`${server}/api/admin/users-subscription?${query.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
      },
    });

    if (!result.ok) throw new Error("Failed to fetch subscriptions");

    return result.json();
  },

  // get all plans
  getAllPlans: async () => {
    const result = await fetch(`${server}/api/subscription/plans`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    return result.json();
  },

  // get customer plans detail
  getCustomerSubscription: async () => {
    const result = await fetch(`${server}/api/customer-subscription/plan`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
      },
    });

    return result.json();
  },

  cancelSubscriptionService: async (formData: CancelSubscriptionPayload): Promise<CancelSubscriptionResponse> => {
    try {
      const response = await fetch(`${server}/api/admin/cancel-user-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      }

      const data: CancelSubscriptionResponse = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Failed to cancel subscription");
      }

      return data;
    } catch (error: any) {
      console.error("Subscription cancellation error:", error);
      toast.error(`Erreur lors de l'annulation: ${error.message || "Une erreur est survenue"}`);
      throw error;
    }
  },

  adminDeletePlan: async (id = 1, product_id = "") => {
    const query = new URLSearchParams({ id: id.toString() });
    if (product_id) {
      query.append("product_id", product_id);
    }

    const result = await fetch(`${server}/api/admin/plan?${query.toString()}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
      },
    });

    return result.json();
  },

  getSubscriptionDetails: async () => {
    try {
      const response = await fetch(`${server}/api/customer-subscription`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (!data.sessionUrl) {
        throw new Error("No session URL returned from server");
      }
      return data.sessionUrl;
    } catch (error: any) {
      console.error("Subscription error:", error);
      toast.error(`Erreur lors de l'abonnement: ${error.message || "Une erreur est survenue"}`);
      throw error;
    }
  },

  refundSubscriptionService: async (formData: RefundSubscriptionPayload): Promise<any> => {
    try {
      const response = await fetch(`${server}/api/admin/subscription-refund`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token") ?? ""}`,
        },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      }

      const data: any = await response.json();
      if (data.success !== "ok") {
        throw new Error(data.message || "Failed to refund subscription");
      }

      return data;
    } catch (error: any) {
      toast.error(` ${error.message || "Une erreur est survenue"}`);
      throw error;
    }
  },
};
